import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Banknote,
  History,
  Milk,
  RefreshCw,
  ShoppingBag,
  Wallet,
} from "lucide-react";
import {
  fetchBalance,
  fetchBalanceEntries,
  fetchEntries,
} from "../services/dataService";
import api from "../services/api";
import LoadingScreen from "./reusable/loadingScreen";
import {
  calculateLedgerBalance,
  getTransactionContribution,
  LEDGER_PERSPECTIVE,
  toAmount,
} from "../utils/balanceCalculation";
import { useTranslation } from "react-i18next";

const EMPTY_SHEET = {
  morning: { total: 0, weight: 0 },
  evening: { total: 0, weight: 0 },
  borrow: [],
};

const formatAmount = (amount) =>
  `${amount >= 0 ? "+" : "−"} ₹${Math.abs(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value, language, fallback) => {
  if (!value) return fallback;
  const normalized = String(value).slice(0, 10);
  const parsed = new Date(`${normalized}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) return normalized;
  return parsed.toLocaleDateString(
    language?.startsWith("hi") ? "hi-IN" : "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    },
  );
};

const getItemLabel = (item, t) => {
  const normalized = String(item || "").trim().toLowerCase();

  if (["money received", "money recieved", "receive money"].includes(normalized)) {
    return t("balanceSheet.items.money_received");
  }
  if (["money given", "give money"].includes(normalized)) {
    return t("balanceSheet.items.money_given");
  }
  if (normalized === "feed") return t("balanceSheet.items.feed");
  if (normalized === "ghee") return t("balanceSheet.items.ghee");
  return item || t("balanceSheet.items.unknown");
};

const SummaryCard = ({
  label,
  value,
  detail,
  tone = "neutral",
  className = "",
}) => {
  const toneClass =
    tone === "credit"
      ? "text-credit"
      : tone === "debit"
        ? "text-debit"
        : "text-primary";

  return (
    <div
      className={`min-w-0 rounded-xl border border-line bg-white p-3 shadow-sm ${className}`}
    >
      <p className="text-[9px] font-bold uppercase tracking-widest text-[#6B7280]">
        {label}
      </p>
      <p
        className={`mt-1 break-words font-mono text-[14px] font-bold leading-tight ${toneClass}`}
      >
        {value}
      </p>
      {detail && (
        <p className="mt-1 text-[10.5px] font-medium text-[#6B7280]">
          {detail}
        </p>
      )}
    </div>
  );
};

function AdminBalanceDashboard({ startDate, endDate }) {
  const { t, i18n } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [sheet, setSheet] = useState(EMPTY_SHEET);
  const [milkEntries, setMilkEntries] = useState({ morning: [], evening: [] });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const loadDashboard = async () => {
    if (!startDate || !endDate) return;
    const requestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setError(false);

      const [nextSummary, nextSheet, entriesData, usersResponse] =
        await Promise.all([
          fetchBalance(startDate, endDate),
          fetchBalanceEntries(startDate, endDate),
          fetchEntries(startDate, endDate),
          api.get("/users").catch(() => ({ data: [] })),
        ]);

      if (requestId !== requestIdRef.current) return;

      setSummary(nextSummary);
      setSheet({ ...EMPTY_SHEET, ...nextSheet });
      setMilkEntries({
        morning: entriesData?.morningEntries || [],
        evening: entriesData?.eveningEntries || [],
      });
      setCustomers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
    } catch (loadError) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to load admin balance dashboard:", loadError);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadDashboard();

    return () => {
      requestIdRef.current += 1;
    };
  }, [startDate, endDate]);

  const ledger = useMemo(
    () =>
      calculateLedgerBalance(
        {
          openingBalance: summary?.Before?.total,
          milk:
            Number(sheet.morning?.total || 0) +
            Number(sheet.evening?.total || 0),
          feed: summary?.feed?.totalMoney,
          ghee: summary?.ghee?.totalMoney,
          moneyReceived: summary?.moneyReceived?.totalMoney,
          moneyGiven: summary?.moneyGiven?.totalMoney,
        },
        LEDGER_PERSPECTIVE.ADMIN,
      ),
    [sheet, summary],
  );

  const customerNames = useMemo(
    () =>
      new Map(
        customers.map((customer) => [String(customer.id), customer.name]),
      ),
    [customers],
  );

  const activities = useMemo(() => {
    const toMilkActivity = (entry, session) => ({
      key: `${session}-${entry.id}`,
      date: entry.date,
      title: t(
        session === "morning"
          ? "balanceSheet.items.milk_m"
          : "balanceSheet.items.milk_e",
      ),
      person:
        customerNames.get(String(entry.user_id)) ||
        (entry.user_id
          ? t("adminBalance.customer_number", { id: entry.user_id })
          : t("adminBalance.dairy_entry")),
      detail: t("adminBalance.milk_detail", {
        weight: Number(entry.weight || 0).toFixed(2),
        fat: Number(entry.fat || 0).toFixed(1),
      }),
      amount: toAmount(entry.total),
      kind: "milk",
    });

    const milkActivity = [
      ...milkEntries.morning.map((entry) =>
        toMilkActivity(entry, "morning"),
      ),
      ...milkEntries.evening.map((entry) =>
        toMilkActivity(entry, "evening"),
      ),
    ];

    const borrowActivity = (sheet.borrow || []).map((entry) => {
      const entryName = String(entry.name || "").trim();
      const quantityDetail =
        entry.quantity && entry.price
          ? t("adminBalance.quantity_detail", {
              quantity: entry.quantity,
              price: Number(entry.price).toFixed(2),
            })
          : t("adminBalance.money_entry");

      return {
        key: `borrow-${entry.id}`,
        date: entry.date,
        title: getItemLabel(entry.item, t),
        person:
          !entryName || entryName.toLowerCase() === "dairy"
            ? t("adminBalance.dairy")
            : entryName,
        detail: quantityDetail,
        amount: getTransactionContribution(
          entry.item,
          entry.money,
          LEDGER_PERSPECTIVE.ADMIN,
        ),
        kind: "transaction",
      };
    });

    return [...milkActivity, ...borrowActivity].sort((a, b) => {
      const dateDifference =
        new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
      return dateDifference || String(b.key).localeCompare(String(a.key));
    });
  }, [customerNames, milkEntries, sheet.borrow, t]);

  const morningMilk = toAmount(sheet.morning?.total);
  const eveningMilk = toAmount(sheet.evening?.total);
  const totalMilkWeight =
    Number(sheet.morning?.weight || 0) + Number(sheet.evening?.weight || 0);

  const breakdown = [
    {
      label: t("balanceSheet.items.milk_m"),
      value: morningMilk,
      detail: t("adminBalance.weight_detail", {
        weight: Number(sheet.morning?.weight || 0).toFixed(2),
      }),
    },
    {
      label: t("balanceSheet.items.milk_e"),
      value: eveningMilk,
      detail: t("adminBalance.weight_detail", {
        weight: Number(sheet.evening?.weight || 0).toFixed(2),
      }),
    },
    {
      label: t("balanceSheet.items.money_received"),
      value: ledger.contributions.moneyReceived,
      detail: t("adminBalance.money_in"),
    },
    {
      label: t("balanceSheet.items.money_given"),
      value: ledger.contributions.moneyGiven,
      detail: t("adminBalance.money_out"),
    },
    {
      label: t("balanceSheet.items.feed"),
      value: ledger.contributions.feed,
      detail: t("adminBalance.item_given"),
    },
    {
      label: t("balanceSheet.items.ghee"),
      value: ledger.contributions.ghee,
      detail: t("adminBalance.item_given"),
    },
  ];

  if (loading && !summary) return <LoadingScreen />;

  if (error) {
    return (
      <div className="rounded-2xl border border-debit/20 bg-white p-6 text-center shadow-sm">
        <p className="text-[13px] font-bold text-debit">
          {t("adminBalance.error")}
        </p>
        <button
          type="button"
          onClick={loadDashboard}
          className="mt-4 inline-flex min-h-[42px] items-center gap-2 rounded-xl bg-primary px-4 py-2 text-[12px] font-bold text-white"
        >
          <RefreshCw className="h-4 w-4" />
          {t("adminBalance.retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-2xl bg-primary p-5 text-white shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/65">
              {t("adminBalance.total_balance")}
            </p>
            <p className="mt-2 font-mono text-[27px] font-black leading-none">
              {formatAmount(ledger.closingBalance)}
            </p>
            <p className="mt-2 text-[11px] font-medium text-white/70">
              {t("adminBalance.summary_line", {
                opening: formatAmount(ledger.openingBalance),
                period: formatAmount(ledger.activity),
              })}
            </p>
          </div>
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/10">
            <Wallet className="h-5 w-5" aria-hidden="true" />
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-2.5">
        <SummaryCard
          label={t("adminBalance.previous_balance")}
          value={formatAmount(ledger.openingBalance)}
          tone={ledger.openingBalance >= 0 ? "credit" : "debit"}
        />
        <SummaryCard
          label={t("adminBalance.selected_dates")}
          value={formatAmount(ledger.activity)}
          tone={ledger.activity >= 0 ? "credit" : "debit"}
        />
        <SummaryCard
          label={t("adminBalance.total_milk")}
          value={t("adminBalance.weight_detail", {
            weight: totalMilkWeight.toFixed(1),
          })}
          detail={t("adminBalance.entry_count", {
            count: activities.length,
          })}
          className="col-span-2"
        />
      </section>

      <section className="rounded-2xl border border-line bg-paper/50 p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Banknote className="h-4 w-4 text-primary" aria-hidden="true" />
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-primary">
            {t("adminBalance.money_details")}
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {breakdown.map((item) => (
            <SummaryCard
              key={item.label}
              label={item.label}
              value={formatAmount(item.value)}
              detail={item.detail}
              tone={item.value >= 0 ? "credit" : "debit"}
            />
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-line bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-line bg-paper/40 px-4 py-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-primary" aria-hidden="true" />
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-primary">
              {t("adminBalance.all_entries")}
            </h2>
          </div>
          <span className="rounded-full bg-primary/10 px-2 py-1 text-[10px] font-bold text-primary">
            {activities.length}
          </span>
        </div>

        {activities.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <p className="text-[13px] font-bold text-primary">
              {t("adminBalance.no_entries_title")}
            </p>
            <p className="mt-1 text-[11.5px] text-[#6B7280]">
              {t("adminBalance.no_entries_body")}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-line/70">
            {activities.map((activity) => (
              <div
                key={activity.key}
                className="flex items-center gap-3 px-4 py-3.5"
              >
                <div
                  className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                    activity.kind === "milk"
                      ? "bg-primary/10 text-primary"
                      : "bg-amber/10 text-amber"
                  }`}
                >
                  {activity.kind === "milk" ? (
                    <Milk className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ShoppingBag className="h-4 w-4" aria-hidden="true" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-[12.5px] font-bold text-primary">
                      {activity.title}
                    </p>
                    <span className="flex-shrink-0 text-[9.5px] font-medium text-[#6B7280]">
                      {formatDate(
                        activity.date,
                        i18n.resolvedLanguage,
                        t("balanceSheet.details.not_available"),
                      )}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-[10.5px] font-medium text-[#6B7280]">
                    {activity.person} · {activity.detail}
                  </p>
                </div>
                <p
                  className={`flex-shrink-0 whitespace-nowrap font-mono text-[12.5px] font-bold ${
                    activity.amount >= 0 ? "text-credit" : "text-debit"
                  }`}
                >
                  {formatAmount(activity.amount)}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default AdminBalanceDashboard;
